import { useState } from "react";

// Inline SVG icons to avoid lucide-react dependency issues
const IconX = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const IconSparkles = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
  </svg>
);

export interface OptionalAppealDetails {
  clinicalIssue?: string;
  durationSeverity?: string;
  triedOrContext?: string;
}

interface StrengthenAppealModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (details: OptionalAppealDetails) => void;
  onSkip: () => void;
}

// Universal chips - NOT specialty-specific
const CLINICAL_ISSUE_CHIPS = [
  "Persistent symptoms",
  "Worsening symptoms",
  "Functional limitation",
  "New onset",
  "Recurrent",
];

const DURATION_SEVERITY_CHIPS = [
  "2 weeks",
  "6 weeks",
  "3 months",
  "Progressive",
  "Severe",
];

const CONTEXT_CHIPS = [
  "Failed conservative treatment",
  "No improvement with first-line therapy",
  "Contraindication present",
  "Symptoms affecting daily function",
  "Requires diagnostic clarification",
];

export default function StrengthenAppealModal({
  isOpen,
  onClose,
  onGenerate,
  onSkip,
}: StrengthenAppealModalProps) {
  const [details, setDetails] = useState<OptionalAppealDetails>({
    clinicalIssue: "",
    durationSeverity: "",
    triedOrContext: "",
  });

  if (!isOpen) return null;

  const updateField = (
    field: keyof OptionalAppealDetails,
    value: string
  ) => {
    setDetails((prev) => ({ ...prev, [field]: value }));
  };

  const appendChip = (field: keyof OptionalAppealDetails, chip: string) => {
    setDetails((prev) => {
      const current = prev[field] || "";
      const newValue = current ? `${current}; ${chip}` : chip;
      return { ...prev, [field]: newValue };
    });
  };

  const handleGenerate = () => {
    // Only pass non-empty fields
    const cleanedDetails: OptionalAppealDetails = {};
    if (details.clinicalIssue?.trim()) {
      cleanedDetails.clinicalIssue = details.clinicalIssue.trim();
    }
    if (details.durationSeverity?.trim()) {
      cleanedDetails.durationSeverity = details.durationSeverity.trim();
    }
    if (details.triedOrContext?.trim()) {
      cleanedDetails.triedOrContext = details.triedOrContext.trim();
    }
    onGenerate(cleanedDetails);
  };

  const handleSkip = () => {
    onSkip();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-start justify-between rounded-t-xl">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-50 rounded-lg">
              <IconSparkles className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                Strengthen your appeal
              </h2>
              <p className="text-sm text-slate-500 mt-0.5">
                Adding 1–2 details can increase approval odds
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <IconX className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-5">
          <p className="text-sm text-slate-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
            Only include facts you are confident are accurate. All fields are optional.
          </p>

          {/* Field 1: Clinical Issue */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Primary clinical issue
              <span className="font-normal text-slate-400 ml-1">(optional)</span>
            </label>
            <input
              type="text"
              value={details.clinicalIssue || ""}
              onChange={(e) => updateField("clinicalIssue", e.target.value)}
              placeholder='e.g., "Chest pain", "Abnormal bleeding", "Back pain"'
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
            <div className="flex flex-wrap gap-1.5 mt-2">
              {CLINICAL_ISSUE_CHIPS.map((chip) => (
                <button
                  key={chip}
                  type="button"
                  onClick={() => appendChip("clinicalIssue", chip)}
                  className="px-2.5 py-1 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-full transition-colors"
                >
                  {chip}
                </button>
              ))}
            </div>
          </div>

          {/* Field 2: Duration/Severity */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Duration or severity
              <span className="font-normal text-slate-400 ml-1">(optional)</span>
            </label>
            <input
              type="text"
              value={details.durationSeverity || ""}
              onChange={(e) => updateField("durationSeverity", e.target.value)}
              placeholder='e.g., "6 weeks", "Worsening", "Severe", "Recurrent"'
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
            <div className="flex flex-wrap gap-1.5 mt-2">
              {DURATION_SEVERITY_CHIPS.map((chip) => (
                <button
                  key={chip}
                  type="button"
                  onClick={() => appendChip("durationSeverity", chip)}
                  className="px-2.5 py-1 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-full transition-colors"
                >
                  {chip}
                </button>
              ))}
            </div>
          </div>

          {/* Field 3: Context/Tried */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Treatments tried or relevant context
              <span className="font-normal text-slate-400 ml-1">(optional)</span>
            </label>
            <input
              type="text"
              value={details.triedOrContext || ""}
              onChange={(e) => updateField("triedOrContext", e.target.value)}
              placeholder='e.g., "Failed conservative therapy", "No improvement with NSAIDs"'
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
            <div className="flex flex-wrap gap-1.5 mt-2">
              {CONTEXT_CHIPS.map((chip) => (
                <button
                  key={chip}
                  type="button"
                  onClick={() => appendChip("triedOrContext", chip)}
                  className="px-2.5 py-1 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-full transition-colors"
                >
                  {chip}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-slate-50 border-t border-slate-100 px-6 py-4 flex items-center justify-between rounded-b-xl">
          <button
            onClick={handleSkip}
            className="text-sm text-slate-500 hover:text-slate-700 font-medium transition-colors"
          >
            Skip (generate anyway)
          </button>
          <button
            onClick={handleGenerate}
            className="px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
          >
            Generate Appeal
          </button>
        </div>
      </div>
    </div>
  );
}